import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Users, DollarSign, Target, Shield } from 'lucide-react';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import RoleBadge from '../../components/ui/RoleBadge';
import { useAuth } from '../../contexts/AuthContext';
import { roleService } from '../../services/roleService';
import { supabase } from '../../lib/supabase';
import { useRealtimeMultiEntity } from '../../hooks/useRealtimeMultiEntity';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';

import PipelineChart from './components/PipelineChart';
import RecentActivities from './components/RecentActivities';


export default function Dashboard() {
  const { profile } = useAuth();
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [teamRoleStats, setTeamRoleStats] = useState([]);
  
  // Read initial state from localStorage
  const getInitialSampleDataState = () => {
    try {
      const stored = localStorage?.getItem(`crm_sample_data_enabled_${profile?.tenant_id}`);
      return stored === 'true';
    } catch {
      return false;
    }
  };
  
  const [showSampleData, setShowSampleData] = useState(getInitialSampleDataState);
  const [sampleDeals, setSampleDeals] = useState([]);
  const [sampleCustomers, setSampleCustomers] = useState([]);

  // Real-time notifications
  const { notifications, unreadCount, isConnected: notificationsConnected } = useRealtimeNotifications();

  // Real-time multi-entity subscription for dashboard data
  const { entityData, isConnected: entitiesConnected } = useRealtimeMultiEntity([
    {
      entity: 'deals',
      initialData: [],
      transformFn: (deal) => ({
        id: deal?.id,
        title: deal?.title,
        value: deal?.value,
        stage: deal?.stage,
        probability: deal?.probability,
        expectedCloseDate: deal?.expected_close_date,
        createdAt: deal?.created_at
      })
    },
    {
      entity: 'customers',
      initialData: [],
      transformFn: (customer) => ({
        id: customer?.id,
        companyName: customer?.company_name,
        contactPerson: customer?.contact_person,
        email: customer?.email,
        subscriptionStatus: customer?.subscription_status,
        createdAt: customer?.created_at
      })
    },
    {
      entity: 'appointments',
      initialData: [],
      transformFn: (appointment) => ({
        id: appointment?.id,
        title: appointment?.title,
        startTime: appointment?.start_time,
        endTime: appointment?.end_time,
        status: appointment?.status,
        createdAt: appointment?.created_at
      })
    },
    {
      entity: 'activities',
      initialData: [],
      transformFn: (activity) => ({
        id: activity?.id,
        title: activity?.title,
        activityType: activity?.activity_type,
        status: activity?.status,
        createdAt: activity?.created_at
      })
    }
  ]);

  // Calculate KPIs from real data
  const kpis = useMemo(() => {
    // Combine real and sample data if sample data is active
    const deals = showSampleData 
      ? [...(entityData?.deals || []), ...sampleDeals]
      : (entityData?.deals || []);
    const customers = showSampleData
      ? [...(entityData?.customers || []), ...sampleCustomers]
      : (entityData?.customers || []);
    
    // Total Revenue (sum of won deals)
    const totalRevenue = deals
      ?.filter(deal => deal?.stage === 'won')
      ?.reduce((sum, deal) => sum + (parseFloat(deal?.value) || 0), 0);
    
    // Active Customers
    const activeCustomers = customers?.filter(c => c?.subscriptionStatus === 'active')?.length || 0;
    
    // Active Deals (not won or lost)
    const activeDeals = deals?.filter(d => d?.stage !== 'won' && d?.stage !== 'lost')?.length || 0;
    
    // Conversion Rate (won deals / total deals)
    const totalDeals = deals?.length || 0;
    const wonDeals = deals?.filter(d => d?.stage === 'won')?.length || 0;
    const conversionRate = totalDeals > 0 ? ((wonDeals / totalDeals) * 100)?.toFixed(1) : 0;
    
    return {
      totalRevenue,
      activeCustomers,
      activeDeals,
      conversionRate
    };
  }, [entityData, showSampleData, sampleDeals, sampleCustomers]);

  useEffect(() => {
    loadCurrentUserRole();
    loadTeamRoleStats();
  }, [profile]);

  // Load sample data if toggle is already active on mount
  useEffect(() => {
    if (showSampleData && profile?.tenant_id) {
      loadSampleData();
    }
  }, [profile?.tenant_id]);

  const loadCurrentUserRole = async () => {
    if (!profile?.id) return;
    try {
      const role = await roleService?.getUserPrimaryRole(profile?.id);
      setCurrentUserRole(role);
    } catch (err) {
      console.error('Error loading user role:', err);
    }
  };

  const loadTeamRoleStats = async () => {
    try {
      const { data: roles, error } = await supabase
        ?.from('system_roles')
        ?.select(`
          id,
          name,
          display_name,
          hierarchy_level,
          user_role_assignments (count)
        `);

      if (error) throw error;

      const stats = roles?.map(role => ({
        ...role,
        userCount: role?.user_role_assignments?.[0]?.count || 0
      }))?.sort((a, b) => b?.hierarchy_level - a?.hierarchy_level);

      setTeamRoleStats(stats);
    } catch (err) {
      console.error('Error loading team role stats:', err);
    }
  };

  const loadSampleData = async () => {
    try {
      const [dealsData, customersData] = await Promise.all([
        sampleDataService?.getSampleDataByEntity('deals', profile?.tenant_id),
        sampleDataService?.getSampleDataByEntity('customers', profile?.tenant_id)
      ]);
      setSampleDeals(dealsData || []);
      setSampleCustomers(customersData || []);
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        await loadSampleData();
      } else {
        setSampleDeals([]);
        setSampleCustomers([]);
      }
    } catch (err) {
      console.error('Error toggling sample data:', err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <ComponentIdBadge 
        id="pg-dashboard-main" 
        name="Dashboard Main Page"
        description="Main dashboard overview page with KPI cards and charts"
        position="top-right"
      />
      
      {/* Real-time connection indicator */}
      {(entitiesConnected || notificationsConnected) && (
        <div className="mb-4 flex items-center gap-2 text-sm text-green-600">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Live data connected</span>
          {unreadCount > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500 text-white rounded-full text-xs">
              {unreadCount} new
            </span>
          )}
        </div>
      )}
      
      {/* Page Header with Current User Role */}
      <div className="mb-8 relative">
        <ComponentIdBadge 
          id="hdr-dashboard-title" 
          name="Dashboard Page Header"
          description="Main title and welcome message section"
          position="inline"
          showLabel={true}
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Welcome back! Here's what's happening with your business.</p>
          </div>
          <div className="flex items-center gap-3">
            <SampleDataToggle
              entityType="dashboard data"
              onToggle={handleSampleDataToggle}
              isActive={showSampleData}
            />
            {currentUserRole && (
              <>
                <span className="text-sm text-gray-600">Your Role:</span>
                <RoleBadge
                  roleName={currentUserRole?.name}
                  displayName={currentUserRole?.displayName}
                  hierarchyLevel={currentUserRole?.hierarchyLevel}
                  size="lg"
                  variant="badge"
                  showIcon={true}
                  showHierarchy={true}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ComponentIdBadge 
            id="crd-kpi-revenue" 
            name="Total Revenue KPI Card"
            description="Displays total revenue with trend indicator"
          />
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <TrendingUp className="w-5 h-5 text-green-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Total Revenue</h2>
            </div>
            <div className="text-sm text-gray-400">
              --
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">
            ${kpis?.totalRevenue?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
          <p className="text-gray-500">From won deals</p>
        </div>

        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ComponentIdBadge 
            id="crd-kpi-customers" 
            name="Total Customers KPI Card"
            description="Shows customer count with growth percentage"
          />
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-blue-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Total Customers</h2>
            </div>
            <div className="text-sm text-gray-400">
              --
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">{kpis?.activeCustomers}</div>
          <p className="text-gray-500">Active customers</p>
        </div>

        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ComponentIdBadge 
            id="crd-kpi-deals" 
            name="Active Deals KPI Card"
            description="Displays active deals pipeline value"
          />
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Target className="w-5 h-5 text-purple-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Active Deals</h2>
            </div>
            <div className="text-sm text-gray-400">
              --
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">{kpis?.activeDeals}</div>
          <p className="text-gray-500">Open deals</p>
        </div>

        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ComponentIdBadge 
            id="crd-kpi-conversion" 
            name="Conversion Rate KPI Card"
            description="Shows conversion rate with performance indicator"
          />
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-yellow-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Conversion Rate</h2>
            </div>
            <div className="text-sm text-gray-400">
              --
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">{kpis?.conversionRate}%</div>
          <p className="text-gray-500">Win rate</p>
        </div>
      </div>

      {/* Charts and Activities Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ComponentIdBadge 
            id="crd-chart-pipeline" 
            name="Pipeline Performance Chart"
            description="Visual representation of sales pipeline stages"
          />
          <PipelineChart deals={entityData?.deals || []} />
        </div>

        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ComponentIdBadge 
            id="crd-activities-recent" 
            name="Recent Activities Feed"
            description="Timeline of recent CRM activities and updates"
          />
          <RecentActivities activities={entityData?.activities || []} />
        </div>
      </div>

      {/* Team Role Distribution Card */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Team Role Distribution</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {teamRoleStats?.map(role => (
            <div key={role?.id} className="bg-gray-50 rounded-lg p-4">
              <RoleBadge
                roleName={role?.name}
                displayName={role?.display_name}
                hierarchyLevel={role?.hierarchy_level}
                size="sm"
                variant="badge"
                showIcon={true}
                showHierarchy={true}
              />
              <div className="mt-3">
                <p className="text-2xl font-bold text-gray-900">{role?.userCount}</p>
                <p className="text-xs text-gray-500">
                  {role?.userCount === 1 ? 'user' : 'users'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}